import type { ITaskRepository } from "@/application/ports/repositories";
import type { ExportDownloadOptions, ExportPreview } from "@/domain/export";
import type { BulkResult } from "@/domain/types/bulk";
import type {
  PendingItemState,
  PendingSummary,
  TaskAggregates,
  TaskAggregatesParams,
  TaskListParams,
  TaskListResponse,
  TaskSort,
} from "@/domain/task";
import { downloadBlob } from "../fileDownload";
import * as sdk from "../generated/sdk.gen";
import { unwrap, unwrapBlobWithFilename } from "../unwrap";

const toSortQuery = (sort: readonly TaskSort[] | undefined) =>
  sort && sort.length > 0
    ? sort.map((entry) => `${entry.key}:${entry.direction}`).join(",")
    : undefined;

export class TaskRepository implements ITaskRepository {
  async listPendingItems(
    params: TaskListParams = {},
    signal?: AbortSignal,
  ): Promise<TaskListResponse> {
    const { sort, ...rest } = params;
    return unwrap(sdk.listPendingItems({ query: { ...rest, sort: toSortQuery(sort) }, signal }));
  }

  async getPendingSummary(signal?: AbortSignal): Promise<PendingSummary> {
    return unwrap(sdk.getPendingItemsSummary({ signal }));
  }

  async getPendingAggregates(
    params: TaskAggregatesParams,
    signal?: AbortSignal,
  ): Promise<TaskAggregates> {
    const { aggregates, ...filters } = params;
    const response = await unwrap(
      sdk.getPendingItemAggregates({
        query: { ...filters, aggregates: aggregates.join(",") },
        signal,
      }),
    );
    return response.aggregates;
  }

  async previewExport(params: TaskListParams): Promise<ExportPreview> {
    const { sort: _sort, limit: _limit, offset: _offset, ...filters } = params;
    return unwrap(sdk.previewPendingItemsExport({ query: filters }));
  }

  async downloadExport(params: TaskListParams, options: ExportDownloadOptions): Promise<void> {
    const { sort, limit: _limit, offset: _offset, ...filters } = params;
    const { blob, filename } = await unwrapBlobWithFilename(
      sdk.exportPendingItems,
      {
        query: {
          ...filters,
          sort: toSortQuery(sort),
          format: options.format,
          columns: options.columns.length > 0 ? options.columns.join(",") : undefined,
        },
      },
      `pending-items.${options.format}`,
    );
    downloadBlob(blob, filename);
  }

  async assign(code: string, assigneeUuid: string | null): Promise<PendingItemState> {
    return unwrap(
      sdk.assignPendingItem({
        path: { pending_item_code: code },
        body: { assignee_uuid: assigneeUuid },
      }),
    );
  }

  async snooze(code: string, snoozeExpiresAt?: string): Promise<PendingItemState> {
    return unwrap(
      sdk.snoozePendingItem({
        path: { pending_item_code: code },
        body: snoozeExpiresAt ? { snooze_expires_at: snoozeExpiresAt } : {},
      }),
    );
  }

  async dismiss(code: string): Promise<PendingItemState> {
    return unwrap(sdk.dismissPendingItem({ path: { pending_item_code: code } }));
  }

  async bulkAssign(codes: readonly string[], assigneeUuid: string | null): Promise<BulkResult> {
    return unwrap(
      sdk.bulkAssignPendingItems({
        body: { pending_item_codes: [...codes], data: { assignee_uuid: assigneeUuid } },
      }),
    );
  }

  async bulkSnooze(codes: readonly string[], snoozeExpiresAt?: string): Promise<BulkResult> {
    return unwrap(
      sdk.bulkSnoozePendingItems({
        body: {
          pending_item_codes: [...codes],
          ...(snoozeExpiresAt ? { data: { snooze_expires_at: snoozeExpiresAt } } : {}),
        },
      }),
    );
  }

  async bulkDismiss(codes: readonly string[]): Promise<BulkResult> {
    return unwrap(sdk.bulkDismissPendingItems({ body: { pending_item_codes: [...codes] } }));
  }
}
